import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listEntities, mapApiEntityListItemToUi } from '../../../services/entities.js';
import { normalizePagedMeta } from '../../../shared/utils/apiResponse.js';
import { apiErrorStatus } from './apiErrorStatus.js';

const EMPTY_META = Object.freeze({
  totalItems: 0,
  totalPages: 0,
  currentPage: 1,
  perPage: 0,
  hasNext: false,
  hasPrevious: false,
  nextCursor: null,
});

/**
 * The records of one Unified Profile type.
 *
 * `fetchIdRef` is not ceremony here: switching type is a single click and the
 * two requests are in flight together, so without the guard the slower of the
 * two wins and the list shows the type the user just navigated away from.
 *
 * 403 and 404 come back as separate flags because the screens render different
 * things for them (CT-5275 / CT-5276), and folding both into `error` would make
 * that distinction impossible to recover downstream.
 */
export function useEntityList({
  entityType,
  page = 1,
  pageSize = 10,
  orderBy,
  orderDirection,
  filters,
  search,
} = {}) {
  const [items, setItems] = useState([]);
  const [filterDefs, setFilterDefs] = useState([]);
  const [sortDefs, setSortDefs] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchList = useCallback(async () => {
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const { body, meta: rawMeta } = await listEntities({
        entityType,
        page,
        pageSize,
        orderBy,
        orderDirection,
        filters,
        search,
      });
      if (requestId !== fetchIdRef.current) return;
      setItems((body?.items ?? []).map(mapApiEntityListItemToUi));
      // The dimensions come down with the rows, so they follow the type the
      // user is actually looking at without a second request to keep in step.
      setFilterDefs(body?.filters ?? []);
      setSortDefs(body?.sorts ?? []);
      setMeta(normalizePagedMeta(rawMeta) ?? EMPTY_META);
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setItems([]);
      setMeta(EMPTY_META);
      setStatus(apiErrorStatus(err));
      setError(err?.message ?? 'Failed to load entities');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType, page, pageSize, orderBy, orderDirection, filters, search]);

  useEffect(() => {
    fetchList();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchList]);

  return useMemo(
    () => ({
      items,
      filterDefs,
      sortDefs,
      meta,
      loading,
      error,
      accessDenied: status === 403,
      notFound: status === 404,
      refetch: fetchList,
    }),
    [items, filterDefs, sortDefs, meta, loading, error, status, fetchList],
  );
}

export default useEntityList;
